import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Alert, Button, Chip, LinearProgress, Step, StepButton, Snackbar, Stepper } from "@mui/material";
import axios from "axios";
import { Auth } from "aws-amplify";
import { useTranslation } from "react-i18next";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";

import AddOrganization from "./AddOrganization";
import AddAdminAndClass from "./AddAdminAndClass";
import AddStories from "./AddStories";

const API_URL = "https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com"

const getProgress = (task) => {
  if (!task.total) return 0;

  return ((task.completed) / task.total) * 100;
};


const getTaskStatus = (status) => {
  switch (status) {
    case 0:
      return {
        label: "Processing",
        color: "info",
      };
    case 1:
      return {
        label: "Completed",
        color: "success",
      };
    case 2:
      return {
        label: "Completed with errors",
        color: "warning",
      };
    case 4:
      return {
        label: "Failed",
        color: "error",
      };
    default:
      return {
        label: "Unknown",
        color: "default",
      };
  }
};

const AddOrganizationWizard = ({ mode = "create", masterAdminInfo, orgsData, setPage }) => {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState([]);

  const getInitialStep = () => {
    switch (mode) {
      case "editAdminAndClass":
        return 1;

      case "editStories":
        return 2;

      default:
        return 0;
    }
  };

  const [activeStep, setActiveStep] = useState(getInitialStep());

  // Shared state for all steps.
  const [wizardData, setWizardData] = useState({
    organization: {
      prefix: orgsData?.org || "",
      name: orgsData?.name || "",
      state: orgsData?.state || "",
      city: orgsData?.city || "",
      emailDomain: orgsData?.emailDomain || "",
      schoolEmailDomain: orgsData?.schoolEmailDomain || "",
      password: "",
      schoolCodeFormat: orgsData?.schoolCodeFormat || "",
      schoolCodeLength: orgsData?.schoolCodeLength || "",
      facilitatorLogin: orgsData?.facLogin || "",
      singleSchool: orgsData?.singleSchool || false,
      testName: "",
      testLangs: [],
    },

    adminAndClass: {

    },

    stories: {
      grade: "",
      lang: "",
      items: [],
      selectedStoryIds: [],
      loaded: false,
      assignedStories: [],
      assignedStoriesLoaded: false,
    },
  });
  const [stepStatus, setStepStatus] = useState({
    0: mode !== "create",
    1: false,
    2: false,
  });
  const [isStep1Locked, setIsStep1Locked] = useState(mode !== "create");
  const [loading, setLoading] = useState(false);
  const [taskJobs, setTaskJobs] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const stepValidatorRef = useRef(null);
  const stepSubmitterRef = useRef(null);

  const registerValidator = useCallback((fn) => {
    stepValidatorRef.current = fn;
  }, []);

  const registerSubmitter = useCallback((fn) => {
    stepSubmitterRef.current = fn;
  }, []);

  const { t } = useTranslation();

  const steps = useMemo(() => [
    t("masterAdmin.addOrganization.title"),
    t("masterAdmin.addAdminAndClass.title"),
    t("masterAdmin.addStories.title"),
  ], [t]);

  const handleToastClose = (event, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  const canAccessStep = (stepIndex) => {
    // Cannot reopen organization after creation.
    if (stepIndex === 0 && isStep1Locked) {
      return false;
    }
    if (mode === "create") {
      // Prevent forward navigation unless current step is saved.
      if (stepIndex > activeStep && !stepStatus[activeStep]) {
        return false;
      }
    }

    return true;
  };

  const handleStepClick = (step) => {
    if (step === activeStep) return;
    if (!canAccessStep(step)) return;

    setActiveStep(step);
  };

  const handleSave = async () => {
    if (loading) return;

    // Validate current step first.
    if (stepValidatorRef.current && !stepValidatorRef.current()) return;

    try {
      setLoading(true);

      if (stepSubmitterRef.current) {
        const success = await stepSubmitterRef.current();

        if (!success) return;

        /*
         * Step 2 and Step 3 both create task jobs.
         * Refresh the task list after successful submission.
         */
        if (activeStep === 1 || activeStep === 2) {
          getTaskJobs();
        }
      }

      setStepStatus((prev) => ({
        ...prev,
        [activeStep]: true,
      }));

      if (activeStep === 0) {
        setIsStep1Locked(true);
      }
    } finally {
      setLoading(false);
    }
  };


  const handleNext = () => {
    setActiveStep((prev) =>
      Math.min(prev + 1, steps.length - 1)
    );
  };

  const handleBack = () => {
    setActiveStep((prev) =>
      Math.max(prev - 1, 0)
    );
  };

  const updateStepData = (step, data) => {
    setWizardData((prev) => ({
      ...prev,
      [step]: data,
    }));
  };

  const getTaskJobs = useCallback(async () => {
    try {
      setLoadingTasks(true);

      const session = await Auth.currentSession();
      const jwtToken = session.idToken.jwtToken;
      const masterAdminId = masterAdminInfo.masterAdmin.masterAdminId;

      const response = await axios.get(
        `${API_URL}/alpha/masterAdmin/${masterAdminId}/tasks?org=${wizardData.organization.prefix}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      setTaskJobs(response.data.tasks || []);

    } catch (err) {
      console.log(err);
    } finally {
      setLoadingTasks(false);
    }
  }, [ masterAdminInfo, wizardData.organization.prefix ]);

  useEffect(() => {
    getTaskJobs();
  }, [getTaskJobs]);

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <AddOrganization
            setToastOpen={setToastOpen}
            setToastType={setToastType}
            masterAdminInfo={masterAdminInfo}
            orgsData={orgsData}
            data={wizardData.organization}
            setData={(data) =>
              updateStepData("organization", data)
            }
            registerValidator={registerValidator}
            registerSubmitter={registerSubmitter}
          />
        );

      case 1:
        return (
          <AddAdminAndClass
            setToastOpen={setToastOpen}
            setToastType={setToastType}
            masterAdminInfo={masterAdminInfo}
            data={wizardData.adminAndClass}
            organization={wizardData.organization}
            setData={(data) =>
              updateStepData("adminAndClass", data)
            }
            registerValidator={registerValidator}
            registerSubmitter={registerSubmitter}
            taskJobs={taskJobs.filter((task) => task.type === 0)} // * Step 2 -> task type 0 * Step 3 -> task type 1
          />
        );

      case 2:
        return (
          <AddStories
            setToastOpen={setToastOpen}
            setToastType={setToastType}
            masterAdminInfo={masterAdminInfo}
            data={wizardData.stories}
            organization={wizardData.organization}
            adminData={wizardData.adminAndClass}
            setData={(data) =>
              updateStepData("stories", data)
            }
            registerValidator={registerValidator}
            registerSubmitter={registerSubmitter}
            taskJobs={taskJobs.filter((task) => task.type === 1)} // * Step 2 -> task type 0 * Step 3 -> task type 1
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={handleToastClose}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastType[0]}
          sx={{ width: "100%" }}
        >
          {toastType[1]}
        </Alert>
      </Snackbar>


      <h2
        style={{
          textAlign: "center",
          color: "#0288D1",
          borderRadius: "15px",
          border: "1px solid",
        }}
      >
        {steps[activeStep]}
      </h2>


      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginTop: "0.5rem",
        }}
      >
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step
              key={label}
              completed={stepStatus[index]}
            >
              <StepButton
                onClick={() => handleStepClick(index)}
                disabled={!canAccessStep(index)}
                disableRipple
                disableTouchRipple
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        <div style={{ marginTop: "2rem" }}>
          {renderStep()}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
          marginTop: "1rem",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          color="info"
          onClick={() =>
            setPage({
              mode: "",
              orgsData: null,
            })
          }
          disabled={loading}
        >
          Cancel
        </Button>

        <div
          style={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variant="outlined"
            disabled={
              loading ||
              activeStep !== 2
            }
            onClick={handleBack}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
          >
            {loading
              ? activeStep === 2
                ? "Adding..."
                : "Saving..."
              : activeStep === 2
              ? "Add Stories"
              : "Save"}
          </Button>

          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                mode === "create" &&
                !stepStatus[activeStep]
              }
            >
              Next
            </Button>
          )}
        </div>
      </div>

      {(activeStep === 1 || activeStep === 2) && (
        <Accordion style={{ marginTop: "1rem" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                paddingRight: "1rem",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "1rem",
                }}
              >
                Previous Task Jobs
                <span
                  style={{
                    marginLeft: "0.5rem",
                    fontSize: "0.9rem",
                    color: "#666",
                    fontWeight: 500,
                  }}
                >
                {/* taskFilter
                  * Step 2 -> task type 0
                  * Step 3 -> task type 1
                */}
                ({taskJobs.filter((task) => task.type === (activeStep === 1 ? 0 : 1)).length})
                </span>
              </h3>

              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  getTaskJobs();
                }}
                disabled={loadingTasks}
              >
                Refresh Tasks
              </Button>
            </div>
          </AccordionSummary>

          <AccordionDetails>
            {loadingTasks ? (
              <p>Loading...</p>
            ) : taskJobs.filter((task) => task.type === (activeStep === 1 ? 0 : 1)).length === 0 ? (
              <p
                style={{
                  margin: 0,
                  fontSize: "0.9rem",
                }}
              >
                {activeStep === 1
                  ? "No previous admin/class upload jobs found."
                  : "No previous stories assign jobs found."}
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* taskFilter
                  * Step 2 -> task type 0
                  * Step 3 -> task type 1
                */}
                {taskJobs.filter((task) => task.type === (activeStep === 1 ? 0 : 1)).map((task) => {
                  const statusInfo =
                    getTaskStatus(task.status);

                  const summaryLine = task.status === 0
                    ? task.type === 1
                      ? `Processing stories for ${task.total} schools`
                      : `Processing ${task.total} schools`
                    : task.completed >= task.total
                      ? "Completed successfully"
                      : `Completed with ${task.failed} failed row(s)`;

                  return (
                    <div
                      key={task.taskId}
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "8px 10px",
                        fontSize: "0.9rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "0.95rem",
                          }}
                        >
                          Task #{task.taskId}
                        </h4>

                        <Chip
                          size="small"
                          label={statusInfo.label}
                          color={statusInfo.color}
                        />
                      </div>

                      <p
                        style={{
                          margin: "6px 0 2px",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        }}
                      >
                        {summaryLine}
                      </p>

                      <details style={{ marginTop: "2px" }}>
                        <summary
                          style={{
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            color: "#666",
                          }}
                        >
                          View Full Log
                        </summary>

                        <pre
                          style={{
                            margin: "8px 0 0",
                            fontSize: "0.78rem",
                            whiteSpace: "pre-wrap",
                            fontFamily: "inherit",
                            color: "#555",
                            background: "#fafafa",
                            padding: "8px",
                            borderRadius: "6px",
                          }}
                        >
                          {task.description}
                        </pre>
                      </details>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "10px",
                          fontSize: "0.8rem",
                          marginBottom: "6px",
                        }}
                      >
                        <span>Total Schools: {task.total}</span>
                        <span>Completed: {task.completed}</span>
                        <span>Failed: {task.failed}</span>
                        <span>{Math.round(getProgress(task))}%</span>
                      </div>

                      <LinearProgress
                        variant="determinate"
                        value={getProgress(task)}
                        sx={{
                          height: 6,
                          borderRadius: 10,
                        }}
                      />

                      <p
                        style={{
                          margin: "6px 0 0",
                          fontSize: "0.75rem",
                          color: "#777",
                        }}
                      >
                        {new Date(
                          task.timestamp
                        ).toLocaleString("en-IN", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default AddOrganizationWizard;